import React, { useEffect, useRef, useState } from 'react';
import { fetchMessages, addMessage, uploadChatImage, deleteMessage } from '../services/cloudMessageService';
import { useAuth } from '../context/AuthContext';
import { formatTime, getRelativeDateLabel, isDifferentDay } from '../lib/dateUtils';
import DateSeparator from '../components/common/DateSeparator';

interface Message {
  $id: string;
  message: string;
  img?: string;
  time?: string; // Appwrite's time field (ISO string)
}

const CloudChatPage: React.FC = () => {
  const { user } = useAuth();
  const [showDenied, setShowDenied] = React.useState(user?.role !== 'admin');
  const [errorMsg] = React.useState('Access Denied: Only admins can access the Cloud Chat.');

  if (showDenied) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full shadow-lg flex flex-col items-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-700 mb-6">{errorMsg}</p>
          <button
            className="px-6 py-2 bg-pink-600 text-white rounded hover:bg-pink-700"
            onClick={() => setShowDenied(false)}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const [messages, setMessages] = useState<Message[]>([]);
  const [pagingLastId, setPagingLastId] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [newMessage, setNewMessage] = useState('');
  const [imgUrl, setImgUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [fullscreenImg, setFullscreenImg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const [optionsOpenId, setOptionsOpenId] = useState<string | null>(null);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState<boolean>(true);

  // Load newest messages page
  const loadMessages = async () => {
    setLoading(true);
    try {
      // fetch newest page (ordered desc by createdAt)
      const res = await fetchMessages(20);
      const docs = res.documents || [];
      const fetchedMsgs: Message[] = (docs as any[]).map(doc => ({
        $id: doc.$id,
        message: doc.message || '',
        img: doc.img,
        time: doc.time || doc.$createdAt,
      }));

      // API gives newest->oldest, but UI expects oldest->newest ordering for scrolling to bottom
      const ordered = fetchedMsgs.reverse();
      setMessages(ordered);
      setPagingLastId(res.lastId);
      setHasMore(res.hasMore);
      // we just loaded newest page, scroll to bottom to show latest
      setShouldScrollToBottom(true);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadMessages();
  }, []);

  useEffect(() => {
    if (shouldScrollToBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setShouldScrollToBottom(false);
    }
  }, [messages, shouldScrollToBottom]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() && !imgUrl) return;
    let uploadedImgUrl = imgUrl;
    if (imageFile) {
      setUploading(true);
      try {
        uploadedImgUrl = await uploadChatImage(imageFile);
        setImgUrl(uploadedImgUrl);
      } catch (err) {
        // handle error
      }
      setUploading(false);
    }
    try {
      const created = await addMessage(newMessage, uploadedImgUrl || undefined);
      // prepend the newly created message so UI shows it immediately
      const newMsg: Message = {
        $id: created.$id,
        message: newMessage,
        img: uploadedImgUrl || created.img,
        time: created.time || created.$createdAt,
      };
      setMessages((prev: Message[]) => {
        // avoid duplicates
        if (prev.some(p => p.$id === newMsg.$id)) return prev;
        return [...prev, newMsg];
      });
      // after adding a new message, auto-scroll to show it
      setShouldScrollToBottom(true);
      setNewMessage('');
      setImgUrl('');
      setImageFile(null);
      setImagePreview(null);
    } catch (e) {
      // handle error
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImgUrl('');
  };

  // Fullscreen image modal
  const FullscreenImageModal = ({ src, onClose }: { src: string; onClose: () => void }) => (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div className="relative max-w-4xl max-h-[90vh] p-4" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-white hover:text-gray-300"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <img
          src={src}
          alt="Full preview"
          className="max-w-full max-h-[80vh] object-contain rounded shadow-lg"
        />
      </div>
    </div>
  );

  return (
    <div style={{minHeight: '95%', maxHeight: '95%', minWidth: '100%', backgroundImage: `url(${'/assets/chatbg.webp'})`, backgroundSize: 'cover', backgroundPosition: 'center' }} className="flex flex-col max-w-3xl mx-auto overflow-y-auto bg-white/80 rounded-lg shadow-lg">
      {fullscreenImg && <FullscreenImageModal src={fullscreenImg} onClose={() => setFullscreenImg(null)} />}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-2 py-4"
        onScroll={async (e: React.UIEvent<HTMLDivElement>) => {
          // infinite scroll: when scrolled to top, load older messages
          const target = e.currentTarget as HTMLDivElement;
          if (target.scrollTop <= 50 && hasMore && !loading) {
            const container = messagesContainerRef.current;
            const prevScrollTop = container?.scrollTop ?? 0;
            const prevScrollHeight = container?.scrollHeight ?? 0;
            try {
              setLoading(true);
              // fetch older page using cursorAfter = pagingLastId
              const res = await fetchMessages(20, pagingLastId);
              const docs = res.documents || [];
              if (docs.length) {
                const fetchedMsgs: Message[] = (docs as any[]).map(doc => ({
                  $id: doc.$id,
                  message: doc.message || '',
                  img: doc.img,
                  time: doc.time || doc.$createdAt,
                }));

                // API returns newest->oldest; these are older than current pages, so we need to prepend
                const ordered = fetchedMsgs.reverse();
                setMessages((prev: Message[]) => {
                  // filter out any duplicates from ordered
                  const orderedFiltered = ordered.filter(o => !prev.some(p => p.$id === o.$id));
                  const next = [...orderedFiltered, ...prev];
                  // after React updates DOM, restore scroll so the viewport stays at the same message
                  requestAnimationFrame(() => {
                    const newScrollHeight = container?.scrollHeight ?? 0;
                    if (container) {
                      container.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
                    }
                  });
                  return next;
                });
                setPagingLastId(res.lastId);
                setHasMore(res.hasMore);
              } else {
                setHasMore(false);
              }
            } catch (err) {
              console.error(err);
            } finally {
              setLoading(false);
            }
          }
        }}
      >
        <div className="flex flex-col gap-2 justify-end">
          {loading ? (
            <div>Loading...</div>
          ) : (
            messages.map((msg, index) => {
              const previousMsg = index > 0 ? messages[index - 1] : null;
              const showDateSeparator = !previousMsg || isDifferentDay(
                msg.time || (messages.find(m => m.$id === msg.$id) as any)?.$createdAt,
                previousMsg.time || (messages.find(m => m.$id === previousMsg.$id) as any)?.$createdAt
              );

              return (
                <React.Fragment key={msg.$id}>
                  {showDateSeparator && (
                    <DateSeparator 
                      label={getRelativeDateLabel(
                        msg.time || (messages.find(m => m.$id === msg.$id) as any)?.$createdAt
                      )} 
                    />
                  )}
                  <div className="flex flex-col items-end group relative">
                    <div
                      className="max-w-xs rounded-lg px-4 py-2 bg-green-100 shadow-md relative chat-bubble flex flex-col items-center"
                      style={{ padding: msg.img ? '12px' : undefined, maxWidth: '50%' }}
                    >
                      {/* Three-dot options icon */}
                      <div
                        className="absolute bottom-1 left-[10px] z-10 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        onClick={() => setOptionsOpenId(optionsOpenId === msg.$id ? null : msg.$id)}
                      >
                        <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <circle cx="5" cy="12" r="2" fill="#555" />
                          <circle cx="12" cy="12" r="2" fill="#555" />
                          <circle cx="19" cy="12" r="2" fill="#555" />
                        </svg>
                      </div>
                      {/* Options menu */}
                      {optionsOpenId === msg.$id && (
                        <div className="absolute bottom-7 left-[10px] bg-white border rounded shadow z-20 min-w-[80px]">
                          <button
                            className="block w-full text-left px-4 py-2 text-sm hover:bg-red-100 text-red-600"
                            onClick={async () => {
                              await deleteMessage(msg.$id);
                              setOptionsOpenId(null);
                              loadMessages();
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                      {/* Image and message content */}
                      {msg.img && (
                        <img
                          src={msg.img}
                          alt="attachment"
                          className="block rounded-lg object-contain cursor-pointer mb-2"
                          style={{ maxWidth: 320, maxHeight: 180, width: '100%' }}
                          onClick={() => setFullscreenImg(msg.img!)}
                        />
                      )}
                      {msg.message && (
                        <div className="text-sm whitespace-pre-line w-full text-left">{msg.message}</div>
                      )}
                      <div className="text-xs text-gray-500 text-right mt-1 w-full">{formatTime(msg.time || (messages.find(m => m.$id === msg.$id) as any)?.$createdAt)}</div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>
      <form
        onSubmit={handleSend}
        className="flex gap-2 p-4 bg-white/90 rounded-b-lg border-t items-center"
        style={{ maxWidth: '100%' }}
      >
        <input
          type="text"
          value={newMessage}
          onChange={e => setNewMessage(e.target.value)}
          placeholder="Type your message..."
          className="flex-1 border rounded-full px-4 py-2 focus:outline-none focus:ring"
        />
        <label className="cursor-pointer flex items-center">
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageChange}
          />
          <span className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded-full ml-2">📎</span>
        </label>
        {imagePreview && (
          <div className="relative ml-2">
            <img src={imagePreview} alt="preview" className="max-h-12 max-w-[72px] rounded shadow object-contain" style={{ aspectRatio: '4/3', background: '#eee' }} />
            <button type="button" onClick={removeImage} className="absolute top-0 right-0 bg-red-500 text-white rounded-full px-1 py-0.5 text-xs">✕</button>
          </div>
        )}
        <button
          type="submit"
          className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-full font-semibold shadow"
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : 'Send'}
        </button>
      </form>
    </div>
  );
};

export default CloudChatPage; 